import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import ConfirmDeleteButton from "@/components/ConfirmDeleteButton";

export default function Tables() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [tables, setTables] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", capacity: 2, section: "" });

  const load = async () => {
    const { data } = await api.get("/tables");
    setTables(data);
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  const create = async () => {
    await api.post("/tables", { ...form, capacity: parseInt(form.capacity) || 2 });
    setOpen(false);
    setForm({ name: "", capacity: 2, section: "" });
    await load();
    toast.success(t("save"));
  };
  const remove = async (id) => {
    try {
      await api.delete(`/tables/${id}`);
      await load();
      toast.success(t("delete"));
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const grouped = tables.reduce((acc, t) => {
    const k = t.section || (lang === "tr" ? "Diğer" : "Sonstige");
    acc[k] = acc[k] || [];
    acc[k].push(t);
    return acc;
  }, {});

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">{t("tables")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{tables.length} · {tables.filter((x) => x.status === "occupied").length} {t("occupied")}</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-table-btn">
          <Plus className="w-4 h-4 mr-2" /> {t("add")}
        </Button>
      </div>

      {Object.entries(grouped).map(([section, list]) => (
        <div key={section}>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">{section}</div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
            {list.map((tb) => {
              const occ = tb.status === "occupied";
              return (
                <Card
                  key={tb.id}
                  onClick={() => (occ ? nav(`/orders/${tb.open_order_id}`) : nav(`/orders`))}
                  data-testid={`table-node-${tb.id}`}
                  className={`border shadow-none cursor-pointer transition-colors ${
                    occ ? "bg-primary/10 border-primary/40" : "hover:bg-muted/40"
                  }`}
                >
                  <CardContent className="p-3 text-center">
                    <div className="font-display font-bold text-lg">{tb.name}</div>
                    <div className="text-[10px] uppercase text-muted-foreground">{tb.capacity} {lang === "tr" ? "kişi" : "Pers."}</div>
                    {occ && (
                      <div className="font-mono text-xs text-primary mt-1">{money(tb.open_order_total)}</div>
                    )}
                    <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                      <ConfirmDeleteButton
                        onConfirm={() => remove(tb.id)}
                        itemName={tb.name}
                        data-testid={`del-table-${tb.id}`}
                        className="h-6 w-6 rounded hover:bg-destructive/10"
                        iconClassName="w-3 h-3 text-muted-foreground"
                      />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("add")} — {t("table")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{lang === "tr" ? "Ad" : "Name"}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="table-name" className="mt-1" />
            </div>
            <div>
              <Label>{t("capacity")}</Label>
              <Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="table-cap" className="mt-1" />
            </div>
            <div>
              <Label>{t("section")}</Label>
              <Input value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={create} data-testid="save-table">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
